import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { supabase } from '../lib/supabase';
import { getAttendeeProfile } from '../lib/api/profiles';
import { getPublishedEvents } from '../lib/api/events';
import type { AttendeeProfileWithDetails, Event, ApplicationStatus } from '../types/database';
import { captureError } from '../lib/capture-error';
import { formatLocalDate, formatLocalDateRange } from '../lib/dates';
import { isContactInfoOnlyEvent } from '../lib/event-application';
import { hasEventEnded, isRegistrationOpen } from '../lib/event-status';
import './DashboardPage.css';

function formatEventDateRange(start: string, end: string): string {
  return formatLocalDateRange(start, end) ?? formatLocalDate(start);
}

interface ApplicationWithEvent {
  id: string;
  status: ApplicationStatus;
  event: Event;
  submitted_at: string | null;
  created_at: string;
}

// WCAG AA compliant status badges (4.5:1 contrast minimum)
const STATUS_BADGES: Record<ApplicationStatus, { label: string; className: string }> = {
  draft: { label: 'Draft', className: 'status-draft' },
  submitted: { label: 'Submitted', className: 'status-submitted' },
  under_review: { label: 'Under Review', className: 'status-under-review' },
  approved: { label: 'Approved', className: 'status-approved' },
  rejected: { label: 'Not Accepted', className: 'status-rejected' },
  waitlisted: { label: 'Waitlisted', className: 'status-waitlisted' },
};

const DashboardPage: React.FC = () => {
  const { user, isAdmin, signOut } = useAuth();
  const [profile, setProfile] = useState<AttendeeProfileWithDetails | null>(null);
  const [applications, setApplications] = useState<ApplicationWithEvent[]>([]);
  const [allEvents, setAllEvents] = useState<Event[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function fetchData() {
      if (!user) return;

      try {
        // Fetch full profile with related data
        const profileData = await getAttendeeProfile(user.id);
        setProfile(profileData);

        // Fetch user's applications with event details
        if (profileData) {
          const { data: appData } = await supabase
            .from('event_applications')
            .select(`
              id,
              status,
              submitted_at,
              created_at,
              event:events(*)
            `)
            .eq('attendee_profile_id', profileData.id)
            .order('created_at', { ascending: false });

          if (appData) {
            setApplications(appData as unknown as ApplicationWithEvent[]);
          }
        }

        // Fetch all published events
        const eventsData = await getPublishedEvents();
        setAllEvents(eventsData);
      } catch (error) {
        captureError(error, 'Error fetching dashboard data');
      } finally {
        setLoading(false);
      }
    }

    fetchData();
  }, [user]);

  const handleSignOut = async () => {
    await signOut();
  };

  // Filter events and applications
  const now = new Date();
  const eventStillUpcoming = (e: Event) => !hasEventEnded(e, now);
  const openEvents = allEvents.filter(e => isRegistrationOpen(e, now));
  const appliedEventIds = new Set(applications.map(a => a.event?.id).filter(Boolean));

  // Registered events (approved for upcoming events)
  const registeredEvents = applications.filter(
    a => a.status === 'approved' && a.event && eventStillUpcoming(a.event)
  );

  // Active applications (draft, submitted, under_review, waitlisted) for upcoming events
  const activeApplications = applications.filter(
    a => ['draft', 'submitted', 'under_review', 'waitlisted'].includes(a.status) && a.event && eventStillUpcoming(a.event)
  );

  // Events not applied to
  const unappliedEvents = openEvents.filter(e => !appliedEventIds.has(e.id));

  const hasFullProfileEvents = unappliedEvents.some(
    (e) => !e.is_legacy_event && !isContactInfoOnlyEvent(e)
  );

  const canApplyToEvent = (event: Event) =>
    !event.is_legacy_event &&
    (profile?.profile_completed || isContactInfoOnlyEvent(event));

  const formatDate = (dateStr: string) =>
    formatLocalDate(dateStr, {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });

  const formatLocation = (event: Event) => {
    const parts = [event.location_city, event.location_state, event.location_country].filter(Boolean);
    return parts.join(', ') || 'TBD';
  };

  if (loading) {
    return (
      <div className="container my-5">
        <div className="text-center">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="container my-5">
      <div className="row">
        <div className="col-12">
          <div className="d-flex justify-content-between align-items-center mb-4">
            <h1>My Dashboard</h1>
            <button onClick={handleSignOut} className="btn btn-outline-secondary">
              <i className="fas fa-sign-out-alt me-2" aria-hidden></i>
              Sign Out
            </button>
          </div>

          {/* Welcome Card */}
          <div className="card mb-4">
            <div className="card-body">
              <h5 className="card-title">
                Welcome{profile ? `, ${profile.first_name}` : ''}!
              </h5>
              <p className="card-text text-muted mb-0">
                {user?.email}
              </p>
              {isAdmin && (
                <span className="badge bg-primary mt-2">Admin</span>
              )}
            </div>
          </div>

          {/* Profile Incomplete Warning */}
          {!profile?.profile_completed && (
            <div className={`alert ${hasFullProfileEvents ? 'alert-warning' : 'alert-info'}`} role="alert">
              <h5 className="alert-heading">
                <i className={`fas ${hasFullProfileEvents ? 'fa-exclamation-triangle' : 'fa-user-edit'} me-2`}></i>
                {hasFullProfileEvents ? 'Complete Your Profile' : 'Finish Your Profile'}
              </h5>
              <p className="mb-2">
                {hasFullProfileEvents
                  ? 'Some events require a complete attendee profile before you can apply.'
                  : 'You can register for quick-signup events now. Finish your profile anytime to add emergency contact, faith information, and more.'}
              </p>
              <Link to="/profile/complete" className={`btn ${hasFullProfileEvents ? 'btn-warning' : 'btn-info'}`}>
                <i className="fas fa-user-edit me-2" aria-hidden></i>
                {hasFullProfileEvents ? 'Complete Profile Now' : 'Finish My Profile'}
              </Link>
            </div>
          )}

          {/* Registered Events Section */}
          <div className="card mb-4">
            <div className="card-header dashboard-registered">
              <h5 className="mb-0">
                <i className="fas fa-check-circle me-2"></i>
                My Upcoming Trips ({registeredEvents.length})
              </h5>
            </div>
            <div className="card-body p-0">
              {registeredEvents.length === 0 ? (
                <div className="p-4 text-center text-muted">
                  <i className="fas fa-calendar-check fa-2x mb-2 d-block opacity-50"></i>
                  No upcoming trips yet. Apply to an event below!
                </div>
              ) : (
                <div className="list-group list-group-flush">
                  {registeredEvents.map(app => (
                    <div key={app.id} className="list-group-item">
                      <div className="d-flex justify-content-between align-items-center">
                        <div>
                          <h6 className="mb-1">{app.event.title}</h6>
                          <small className="text-muted">
                            <i className="fas fa-map-marker-alt me-1"></i>
                            {formatLocation(app.event)}
                            <span className="mx-2">|</span>
                            <i className="fas fa-calendar me-1"></i>
                            {formatEventDateRange(app.event.start_date, app.event.end_date)}
                          </small>
                        </div>
                        <Link to={`/events/${app.event.slug}`} className="btn btn-sm btn-outline-dashboard-success">
                          <i className="fas fa-eye me-1" aria-hidden></i>
                          View Details
                        </Link>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Active Applications Section */}
          <div className="card mb-4">
            <div className="card-header dashboard-applications">
              <h5 className="mb-0">
                <i className="fas fa-file-alt me-2"></i>
                Active Applications ({activeApplications.length})
              </h5>
            </div>
            <div className="card-body p-0">
              {activeApplications.length === 0 ? (
                <div className="p-4 text-center text-muted">
                  <i className="fas fa-file-alt fa-2x mb-2 d-block opacity-50"></i>
                  No pending applications
                </div>
              ) : (
                <div className="list-group list-group-flush">
                  {activeApplications.map(app => (
                    <div key={app.id} className="list-group-item">
                      <div className="d-flex justify-content-between align-items-center">
                        <div>
                          <h6 className="mb-1">
                            {app.event.title}
                            <span className={`badge ${STATUS_BADGES[app.status].className} ms-2`}>
                              {STATUS_BADGES[app.status].label}
                            </span>
                          </h6>
                          <small className="text-muted">
                            <i className="fas fa-map-marker-alt me-1"></i>
                            {formatLocation(app.event)}
                            <span className="mx-2">|</span>
                            <i className="fas fa-calendar me-1"></i>
                            {formatDate(app.event.start_date)}
                          </small>
                        </div>
                        <Link
                          to={app.status === 'draft' ? `/events/${app.event.slug}/apply` : `/my-applications`}
                          className="btn btn-sm btn-outline-dashboard-info"
                        >
                          <i className={`fas ${app.status === 'draft' ? 'fa-pen' : 'fa-list-check'} me-1`} aria-hidden></i>
                          {app.status === 'draft' ? 'Continue Application' : 'View Status'}
                        </Link>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Available Events Section */}
          <div className="card mb-4">
            <div className="card-header dashboard-available">
              <h5 className="mb-0">
                <i className="fas fa-globe me-2"></i>
                Available Events ({unappliedEvents.length})
              </h5>
            </div>
            <div className="card-body p-0">
              {unappliedEvents.length === 0 ? (
                <div className="p-4 text-center text-muted">
                  <i className="fas fa-check fa-2x mb-2 d-block opacity-50"></i>
                  You've applied to all available events!
                </div>
              ) : (
                <div className="list-group list-group-flush">
                  {unappliedEvents.map(event => (
                    <div key={event.id} className="list-group-item">
                      <div className="d-flex justify-content-between align-items-center">
                        <div>
                          <h6 className="mb-1">{event.title}</h6>
                          <small className="text-muted">
                            <i className="fas fa-map-marker-alt me-1"></i>
                            {formatLocation(event)}
                            <span className="mx-2">|</span>
                            <i className="fas fa-calendar me-1"></i>
                            {formatEventDateRange(event.start_date, event.end_date)}
                          </small>
                        </div>
                        <div className="d-flex gap-2">
                          <Link to={`/events/${event.slug}`} className="btn btn-sm btn-outline-secondary">
                            <i className="fas fa-info-circle me-1" aria-hidden></i>
                            Details
                          </Link>
                          {canApplyToEvent(event) && (
                            <Link to={`/events/${event.slug}/apply`} className="btn btn-sm btn-primary">
                              <i className="fas fa-clipboard-check me-1" aria-hidden></i>
                              Apply
                            </Link>
                          )}
                          {event.is_legacy_event && event.legacy_application_url && (
                            <a href={event.legacy_application_url} target="_blank" rel="noopener noreferrer" className="btn btn-sm btn-primary">
                              <i className="fas fa-external-link-alt me-1" aria-hidden></i>
                              Apply
                            </a>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* My Profile Section */}
          <div className="card mb-4">
            <div className="card-header d-flex justify-content-between align-items-center">
              <h5 className="mb-0">
                <i className="fas fa-user me-2"></i>
                My Profile
              </h5>
              <Link to="/profile" className="btn btn-sm btn-outline-primary">
                <i className="fas fa-user-edit me-1" aria-hidden></i>
                Edit Profile
              </Link>
            </div>
            <div className="card-body">
              {!profile ? (
                <div className="text-center text-muted py-3">
                  <i className="fas fa-user-plus fa-2x mb-2 d-block opacity-50"></i>
                  <p className="mb-2">No profile created yet</p>
                  <Link to="/profile/complete" className="btn btn-primary">
                    <i className="fas fa-user-plus me-2" aria-hidden></i>
                    Create Profile
                  </Link>
                </div>
              ) : (
                <div className="row">
                  {/* Personal Info */}
                  <div className="col-md-6 mb-4">
                    <h6 className="text-muted border-bottom pb-2 mb-3">
                      <i className="fas fa-id-card me-2"></i>
                      Personal Information
                    </h6>
                    <dl className="row mb-0 small">
                      <dt className="col-sm-4">Name</dt>
                      <dd className="col-sm-8">{profile.first_name} {profile.last_name}</dd>

                      <dt className="col-sm-4">Email</dt>
                      <dd className="col-sm-8">{profile.email}</dd>

                      <dt className="col-sm-4">Phone</dt>
                      <dd className="col-sm-8">{profile.cell_phone || <span className="text-muted">—</span>}</dd>

                      <dt className="col-sm-4">Gender</dt>
                      <dd className="col-sm-8">{profile.gender || <span className="text-muted">—</span>}</dd>

                      <dt className="col-sm-4">T-Shirt</dt>
                      <dd className="col-sm-8">{profile.tshirt_size || <span className="text-muted">—</span>}</dd>

                      <dt className="col-sm-4">Address</dt>
                      <dd className="col-sm-8">
                        {profile.city && profile.state ? (
                          `${profile.city}, ${profile.state}`
                        ) : (
                          <span className="text-muted">—</span>
                        )}
                      </dd>
                    </dl>
                  </div>

                  {/* Emergency Contact */}
                  <div className="col-md-6 mb-4">
                    <h6 className="text-muted border-bottom pb-2 mb-3">
                      <i className="fas fa-phone-alt me-2"></i>
                      Emergency Contact
                    </h6>
                    {profile.emergency_contact ? (
                      <dl className="row mb-0 small">
                        <dt className="col-sm-4">Name</dt>
                        <dd className="col-sm-8">
                          {profile.emergency_contact.first_name} {profile.emergency_contact.last_name}
                        </dd>
                        <dt className="col-sm-4">Phone</dt>
                        <dd className="col-sm-8">{profile.emergency_contact.phone_number}</dd>
                        <dt className="col-sm-4">Location</dt>
                        <dd className="col-sm-8">
                          {profile.emergency_contact.city && profile.emergency_contact.state ? (
                            `${profile.emergency_contact.city}, ${profile.emergency_contact.state}`
                          ) : (
                            <span className="text-muted">—</span>
                          )}
                        </dd>
                      </dl>
                    ) : (
                      <p className="text-muted small mb-0">Not provided</p>
                    )}
                  </div>

                  {/* Church Info */}
                  <div className="col-md-6 mb-4">
                    <h6 className="text-muted border-bottom pb-2 mb-3">
                      <i className="fas fa-church me-2"></i>
                      Church Information
                    </h6>
                    {profile.church_info ? (
                      <dl className="row mb-0 small">
                        <dt className="col-sm-4">Church</dt>
                        <dd className="col-sm-8">
                          {profile.church_info.church_name || <span className="text-muted">—</span>}
                        </dd>
                        <dt className="col-sm-4">Pastor</dt>
                        <dd className="col-sm-8">
                          {profile.church_info.pastor_first_name} {profile.church_info.pastor_last_name}
                        </dd>
                        <dt className="col-sm-4">Phone</dt>
                        <dd className="col-sm-8">{profile.church_info.phone_number}</dd>
                      </dl>
                    ) : (
                      <p className="text-muted small mb-0">Not provided</p>
                    )}
                  </div>

                  {/* Faith Info */}
                  <div className="col-md-6 mb-4">
                    <h6 className="text-muted border-bottom pb-2 mb-3">
                      <i className="fas fa-praying-hands me-2"></i>
                      Faith Background
                    </h6>
                    {profile.faith_info ? (
                      <dl className="row mb-0 small">
                        <dt className="col-sm-4">Believer</dt>
                        <dd className="col-sm-8">
                          {profile.faith_info.is_born_again_believer ? (
                            <span className="text-success"><i className="fas fa-check me-1"></i>Yes</span>
                          ) : (
                            <span className="text-muted">—</span>
                          )}
                        </dd>
                        <dt className="col-sm-4">How Long</dt>
                        <dd className="col-sm-8">
                          {profile.faith_info.believer_length || <span className="text-muted">—</span>}
                        </dd>
                        <dt className="col-sm-4">Mission Trips</dt>
                        <dd className="col-sm-8">
                          {profile.faith_info.has_been_on_mission_trip ? (
                            <span className="text-success"><i className="fas fa-check me-1"></i>Yes</span>
                          ) : (
                            'No'
                          )}
                        </dd>
                        <dt className="col-sm-4">Evangelism</dt>
                        <dd className="col-sm-8">
                          {profile.faith_info.currently_practicing_evangelism ? (
                            <span className="text-success"><i className="fas fa-check me-1"></i>Active</span>
                          ) : (
                            <span className="text-muted">—</span>
                          )}
                        </dd>
                      </dl>
                    ) : (
                      <p className="text-muted small mb-0">Not provided</p>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Admin Section */}
          {isAdmin && (
            <div className="card bg-light">
              <div className="card-body">
                <h5 className="card-title">
                  <i className="fas fa-tools me-2"></i>
                  Admin Tools
                </h5>
                <div className="row g-3 mt-2">
                  <div className="col-md-6">
                    <Link to="/admin" className="btn btn-outline-primary w-100">
                      <i className="fas fa-calendar-plus me-2"></i>
                      Manage Events
                    </Link>
                  </div>
                  <div className="col-md-6">
                    <Link to="/admin" className="btn btn-outline-primary w-100">
                      <i className="fas fa-list-check me-2"></i>
                      Review Applications
                    </Link>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default DashboardPage;
